import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { addSuccessNotification, addErrorNotification } from '../common/notification/notificationReducer';
import type { AppDispatch, SettingsState, Store } from '../store';
import settingsApi from './settingsApi';
import { Setting } from '../common/types/settings';

const initialState: SettingsState = {
  advisors: [],
  groups: [],
  status: null,
  errorMessage: null,
};

const loadAdvisors = createAsyncThunk<
  Setting[],
  undefined,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('settings/loadAdvisors', async (_, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const settings = await settingsApi.loadSettings('advisors');

  if (settings != null) {
    return JSON.parse(settings.settings);
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Einstellungen konnten nicht geladen werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return [];
});

const loadGroups = createAsyncThunk<
  Setting[],
  undefined,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('settings/loadGroups', async (_, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const settings = await settingsApi.loadSettings('groups');
  if (settings != null) {
    return JSON.parse(settings.settings);
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Einstellungen konnten nicht geladen werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return [];
});

const updateAdvisors = createAsyncThunk<
  boolean,
  Setting[],
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('settings/updateAdvisors', async (newValues, { dispatch, getState, rejectWithValue }) => {
  const state = getState();

  const isEdited = await settingsApi.updateSettings('advisors', JSON.stringify(newValues));
  if (isEdited) {
    dispatch(addSuccessNotification('Die Einstellungen wurden aktualisiert.'));
    return isEdited;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Einstellungen konnten nicht aktualisiert werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const updateGroups = createAsyncThunk<
  boolean,
  Setting[],
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('settings/updateGroups', async (newValues, { dispatch, getState, rejectWithValue }) => {
  const state = getState();

  const isEdited = await settingsApi.updateSettings('groups', JSON.stringify(newValues));
  if (isEdited) {
    dispatch(addSuccessNotification('Die Einstellungen wurden aktualisiert.'));
    return isEdited;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Einstellungen konnten nicht aktualisiert werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAdvisors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadAdvisors.fulfilled, (state, action) => {
        state.status = 'idle';
        state.advisors = action.payload;
      })
      .addCase(loadAdvisors.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(loadGroups.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadGroups.fulfilled, (state, action) => {
        state.status = 'idle';
        state.groups = action.payload;
      })
      .addCase(loadGroups.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(updateAdvisors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateAdvisors.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(updateAdvisors.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(updateGroups.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateGroups.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(updateGroups.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase('resetState', (state) => {
        state = initialState;
      });
  },
});

export { loadAdvisors, loadGroups, updateAdvisors, updateGroups };

export default settingsSlice.reducer;
